import type {AgentWireEvent} from './agentWireEvent'
import type {
  ChunkCompleteMetadata,
  ExecuteDebug,
  KbSource,
  InternetSource,
  TokenUsage,
  CloudUnits,
  StepPayload,
} from './BedrockSocketProvider'
import {normalizeContextUsage, type ContextUsage} from './omicsContextUsage'
import type {AgentAnswerFormat} from './formatAgentXmlToMarkdown'
import {filesChangedFields} from './filesChanged'

interface RawBedrockFrame {
  type: string
  messageId?: string
  sessionId?: string
  conversationId?: string
  conversation_id?: string
  error?: string
  content?: string
  delta?: boolean | Record<string, unknown>
  stepIndex?: number
  node?: string
  segmentKind?: string
  debug?: ExecuteDebug
  runId?: string
  response?: string
  answer?: string
  timeline?: Record<string, unknown>
  toolsUsed?: string[]
  tools_used?: string[]
  suggestedFollowups?: string[]
  suggested_followups?: string[]
  pendingRuns?: Array<{run_id: string; tool?: string; state?: string}>
  resolvedRuns?: string[]
  resolved_runs?: string[]
  pending_runs?: Array<{run_id: string; tool?: string; state?: string}>
  toolCallCounts?: Record<string, number>
  tool_call_counts?: Record<string, number>
  tokenUsage?: TokenUsage
  token_usage?: TokenUsage
  modelUsed?: string
  model_used?: string
  kbSources?: KbSource[]
  kb_sources?: KbSource[]
  internetSources?: InternetSource[]
  internet_sources?: InternetSource[]
  cloudUnits?: CloudUnits
  cloud_units?: CloudUnits
  balanceRemaining?: number
  balance_remaining?: number
  agentRuntimeLabel?: string
  agent_runtime_label?: string
  answerFormat?: AgentAnswerFormat
  answer_format?: AgentAnswerFormat
  contextUsage?: ContextUsage
  context_usage?: ContextUsage
  contextCompacted?: boolean
  context_compacted?: boolean
  interrupted?: boolean
  filesChanged?: unknown
  files_changed?: unknown
  filesChangedTotal?: unknown
  files_changed_total?: unknown
  filesChangedTruncated?: unknown
  files_changed_truncated?: unknown
  errorCode?: string
  error_code?: string
  canResume?: boolean
  can_resume?: boolean
  toolCallId?: string
  tool_call_id?: string
  toolName?: string
  tool_name?: string
  toolInput?: Record<string, unknown>
  tool_input?: Record<string, unknown>
  displaySummary?: string
  display_summary?: string
  result?: string
  ok?: boolean
}

function wireIds(msg: RawBedrockFrame) {
  return {
    conversationId: msg.conversationId ?? msg.conversation_id,
    sessionId: msg.sessionId,
    messageId: msg.messageId,
  }
}

/** Available cloud units reported by the agent on terminal frames (after the turn, or at the gate). */
function balanceEvent(message: RawBedrockFrame, ids: ReturnType<typeof wireIds>): AgentWireEvent[] {
  const balanceRemaining = message.balanceRemaining ?? message.balance_remaining
  return typeof balanceRemaining === 'number' && Number.isFinite(balanceRemaining)
    ? [{type: 'balance', ...ids, balanceRemaining}]
    : []
}

export function parseBedrockWireEvents(message: RawBedrockFrame): AgentWireEvent[] {
  const ids = wireIds(message)
  const events: AgentWireEvent[] = []

  switch (message.type) {
    case 'RUN_STARTED':
      events.push({type: 'run_started', ...ids})
      break

    case 'RUN_DETACHED':
      events.push({type: 'run_detached', ...ids})
      break

    case 'TEXT_MESSAGE_CONTENT': {
      const receivedMessageId = message.messageId || message.sessionId || 'unknown'
      if (message.delta === true) {
        if (message.content) {
          const sk = message.segmentKind as ChunkCompleteMetadata['segmentKind'] | undefined
          const si = message.stepIndex
          events.push({
            type: 'chunk',
            ...ids,
            messageId: receivedMessageId,
            chunk: message.content,
            isComplete: false,
            metadata: {
              ...(sk ? {segmentKind: sk} : {}),
              ...(si !== undefined && si !== null ? {stepIndex: si} : {}),
            },
          })
        }
      } else if (message.stepIndex !== undefined && message.node) {
        const seg = message.segmentKind as StepPayload['segmentKind'] | undefined
        events.push({
          type: 'step',
          ...ids,
          messageId: receivedMessageId,
          step: {
            type: 'step',
            stepIndex: message.stepIndex,
            node: message.node,
            content: message.content,
            ...(seg && {segmentKind: seg}),
            debug: message.debug,
            messageId: message.messageId,
            sessionId: message.sessionId,
          },
        })
      }
      break
    }

    case 'TOOL_CALL_START':
      events.push({
        type: 'step',
        ...ids,
        step: {
          type: 'tool_call_start',
          stepIndex: message.stepIndex!,
          toolCallId: message.toolCallId ?? message.tool_call_id!,
          toolName: message.toolName ?? message.tool_name!,
          toolInput: message.toolInput ?? message.tool_input ?? {},
          displaySummary: message.displaySummary ?? message.display_summary,
          messageId: message.messageId,
          sessionId: message.sessionId,
        },
      })
      break

    case 'TOOL_CALL_END':
      events.push({
        type: 'step',
        ...ids,
        step: {
          type: 'tool_call_end',
          stepIndex: message.stepIndex!,
          toolCallId: message.toolCallId ?? message.tool_call_id!,
          toolName: message.toolName ?? message.tool_name!,
          toolResult: message.result ?? '',
          toolOk: message.ok !== false,
          toolError: message.error,
          messageId: message.messageId,
          sessionId: message.sessionId,
        },
      })
      break

    case 'RUN_CANCELLED': {
      const mid = message.messageId || message.sessionId || 'unknown'
      events.push({
        type: 'chunk',
        ...ids,
        messageId: mid,
        chunk: '',
        isComplete: true,
        metadata: {
          // A cancelled turn keeps what it already wrote (file-ops contract, section 3).
          ...filesChangedFields(message),
          streamAlert: {kind: 'cancel', message: message.error || 'Request canceled'},
        },
      })
      break
    }

    case 'RUN_INTERRUPTED': {
      const receivedMessageId = message.messageId || message.sessionId || 'unknown'
      const toolsUsed = message.toolsUsed ?? message.tools_used
      const tokenUsage = message.tokenUsage ?? message.token_usage
      const modelUsed = message.modelUsed ?? message.model_used
      const agentRuntimeLabel = message.agentRuntimeLabel ?? message.agent_runtime_label
      const cloudUnits = message.cloudUnits ?? message.cloud_units
      const timeline = message.timeline
      const contextUsage = normalizeContextUsage(message.contextUsage ?? message.context_usage)
      const contextCompacted = Boolean(message.contextCompacted ?? message.context_compacted)
      const fileChanges = filesChangedFields(message)
      events.push(...balanceEvent(message, ids))
      if (contextUsage) {
        events.push({type: 'context_usage', ...ids, messageId: receivedMessageId, contextUsage})
      }
      events.push({
        type: 'chunk',
        ...ids,
        messageId: receivedMessageId,
        chunk: '',
        isComplete: true,
        metadata: {
          answer: message.response || message.answer || '',
          ...(timeline && {timeline}),
          ...(toolsUsed?.length && {toolsUsed}),
          ...(tokenUsage?.total_tokens && {tokenUsage}),
          ...(modelUsed && {modelUsed}),
          ...(agentRuntimeLabel && {agentRuntimeLabel}),
          ...(cloudUnits?.units && {cloudUnits}),
          ...(contextUsage && {contextUsage}),
          ...(contextCompacted && {contextCompacted: true}),
          ...fileChanges,
          streamAlert: {kind: 'cancel', message: 'Response stopped by user'},
          interrupted: true,
        },
      })
      break
    }

    case 'RUN_FINISHED': {
      const receivedMessageId = message.messageId || message.sessionId || 'unknown'
      const response = message.response || ''
      const answer = message.answer ?? response
      const toolsUsed = message.toolsUsed ?? message.tools_used
      const suggestedFollowups = message.suggestedFollowups ?? message.suggested_followups
      const pendingRuns = message.pendingRuns ?? message.pending_runs
      const resolvedRuns = message.resolvedRuns ?? message.resolved_runs
      const toolCallCounts = message.toolCallCounts ?? message.tool_call_counts
      const tokenUsage = message.tokenUsage ?? message.token_usage
      const modelUsed = message.modelUsed ?? message.model_used
      const kbSources = message.kbSources ?? message.kb_sources
      const internetSources = message.internetSources ?? message.internet_sources
      const cloudUnits = message.cloudUnits ?? message.cloud_units
      const agentRuntimeLabel = message.agentRuntimeLabel ?? message.agent_runtime_label
      const timeline = message.timeline
      const answerFormat = message.answerFormat ?? message.answer_format
      const contextUsage = normalizeContextUsage(message.contextUsage ?? message.context_usage)
      const contextCompacted = Boolean(message.contextCompacted ?? message.context_compacted)
      const fileChanges = filesChangedFields(message)
      events.push(...balanceEvent(message, ids))
      if (contextUsage) {
        events.push({type: 'context_usage', ...ids, messageId: receivedMessageId, contextUsage})
      }
      events.push({
        type: 'chunk',
        ...ids,
        messageId: receivedMessageId,
        chunk: '',
        isComplete: true,
        metadata: {
          answer,
          ...(answerFormat && {answerFormat}),
          ...(timeline && {timeline}),
          ...(toolsUsed?.length && {toolsUsed}),
          ...(suggestedFollowups?.length && {suggestedFollowups}),
          ...(pendingRuns?.length && {pendingRuns}),
          ...(resolvedRuns?.length && {resolvedRuns}),
          ...(toolCallCounts && Object.keys(toolCallCounts).length > 0 && {toolCallCounts}),
          ...(tokenUsage?.total_tokens && {tokenUsage}),
          ...(modelUsed && {modelUsed}),
          ...(kbSources?.length && {kbSources}),
          ...(internetSources?.length && {internetSources}),
          ...(cloudUnits?.units && {cloudUnits}),
          ...(agentRuntimeLabel && {agentRuntimeLabel}),
          ...(contextUsage && {contextUsage}),
          ...(contextCompacted && {contextCompacted: true}),
          ...fileChanges,
        },
      })
      break
    }

    case 'RUN_ERROR': {
      const errCode = message.errorCode ?? message.error_code
      const errMsg = message.error || 'Unknown error'
      // A failed turn can still have written files (file-ops contract, section 3).
      const fileChanges = filesChangedFields(message)
      events.push(...balanceEvent(message, ids))
      if (errCode === 'recursion_limit') {
        events.push({
          type: 'recursion_limit',
          ...ids,
          error: errMsg,
          canResume: message.canResume !== false,
          tokenUsage: message.tokenUsage ?? message.token_usage,
          ...fileChanges,
        })
      } else {
        events.push({type: 'error', ...ids, error: errMsg, ...fileChanges})
      }
      break
    }

    case 'STATE_DELTA': {
      const delta =
        typeof message.delta === 'object' && message.delta !== null
          ? message.delta
          : undefined
      const convId = delta?.conversation_id as string | undefined
      if (convId) {
        events.push({
          type: 'conversation_id',
          conversationId: convId,
          sessionId: message.sessionId,
          messageId: message.messageId,
        })
      }
      const title = typeof delta?.conversation_title === 'string' ? delta.conversation_title.trim() : ''
      if (title) {
        events.push({type: 'conversation_title', ...ids, conversationId: ids.conversationId ?? convId, title})
      }
      break
    }

    case 'error':
      events.push({type: 'error', ...ids, error: message.error || 'Unknown error'})
      break

    case 'cancelled':
      events.push({type: 'stream_cancelled_ack', ...ids, messageId: message.messageId})
      break

    default:
      break
  }

  return events
}
